package com.crowpay.views.components

import com.lightningkite.kiteui.reactive.Constant
import com.lightningkite.kiteui.reactive.LateInitProperty
import com.lightningkite.kiteui.reactive.Property
import com.lightningkite.kiteui.reactive.Readable
import com.lightningkite.kiteui.reactive.StatusListener
import com.lightningkite.kiteui.reactive.addAndRunListener
import com.lightningkite.kiteui.reactive.onRemove
import com.lightningkite.kiteui.reactive.reactive
import com.lightningkite.kiteui.reactive.reactiveScope
import com.lightningkite.kiteui.views.RView
import com.lightningkite.kiteui.views.ViewModifiable
import com.lightningkite.kiteui.views.ViewWriter
import com.lightningkite.kiteui.views.centered
import com.lightningkite.kiteui.views.direct.activityIndicator
import com.lightningkite.kiteui.views.direct.onlyWhen
import com.lightningkite.kiteui.views.direct.stack
import com.lightningkite.kiteui.views.l2.RecyclerViewRenderer
import com.lightningkite.kiteui.views.l2.RecyclerViewRendererSet
import com.lightningkite.kiteui.views.withoutAnimation

private class Cell<T, ID : Any>(
    container: RView,
    private val rendererSet: RecyclerViewRendererSet<T, ID>,
    val data: LateInitProperty<T>,
    id: ID?,
    renderer: RecyclerViewRenderer<T>,
    index: Int
) {
    constructor(
        container: RView,
        set: RecyclerViewRendererSet<T, ID>,
        data: T,
        index: Int
    ) : this(
        container,
        set,
        LateInitProperty<T>().apply { value = data },
        set.id(data),
        set.renderer(data),
        index
    )

    var id: ID? = id
        private set

    var renderer: RecyclerViewRenderer<T> = renderer
        private set

    val index = Constant(index)

    val view = container.stack {
        renderer.render(this, this@Cell.data, this@Cell.index)
    }

    private var watchingLoad = false
    fun loading(readable: Readable<*>) {
        if (watchingLoad) return
        watchingLoad = true
        view.coroutineContext[StatusListener]?.loading(readable)
    }

    fun hide() {
        view.withoutAnimation {
            view.exists = false
        }
    }

    fun newData(value: T) {
        view.exists = true
        if (rendererSet.id(value) == id) data.value = value
        else {
            val r = rendererSet.renderer(value)
            if (r === renderer || r == renderer) data.value = value
            else {
                renderer = r
                view.withoutAnimation {
                    view.clearChildren()
                    data.value = value
                    r.render(view, data, this@Cell.index)
                }
            }
        }
    }
}

fun <T, ID : Any> RView.forEachWithRenderer(
    items: Readable<List<T>>,
    rendererSet: RecyclerViewRendererSet<T, ID>,
    loadingRenderers: List<RecyclerViewRenderer<T>> = emptyList()
) {
    val currentViews = ArrayList<Cell<T, ID>>(
        loadingRenderers.mapIndexed { idx, it ->
            Cell(this@forEachWithRenderer, rendererSet, LateInitProperty(), null, it, idx)
        }
    )

    reactive {
        val list = items()
        for ((idx, item) in list.withIndex()) {
            currentViews.getOrNull(idx)?.newData(item)
                ?: currentViews.add(
                    Cell(this@forEachWithRenderer, rendererSet, item, idx)
                )
        }

        currentViews.forEach { it.loading(this) }

        if (currentViews.size > list.size) {
            currentViews.subList(list.size, currentViews.size).forEach { it.hide() }
        }
    }
}